import type { BoardState, PlayerMarker, Point, Stroke } from './board'
import { themeColors } from './theme'

export type Movement = { id: string; playerId: string; points: Point[]; style?: 'cut' | 'pass' | 'screen' | 'dribble' }
export type Pass = { id: string; from: string; to: string; segmentId?: string; fraction: number }
export type PlayStep = { id: string; movements: Movement[]; passes: Pass[]; notes: string; annotations?: Stroke[] }
export type Play = { initialPlayers: PlayerMarker[]; initialBall: string | null; steps: PlayStep[]; notes: string; setupComplete?: boolean; speed?: number }
export type ScheduledMovement = Movement & { start: number; duration: number; hold: number }
export type ScheduledPass = Pass & { release: number; catchAt: number; origin: Point; target: Point }
export type Schedule = { movements: ScheduledMovement[]; passes: ScheduledPass[]; duration: number; conflicts: string[] }

export const emptyStep = (): PlayStep => ({ id: crypto.randomUUID(), movements: [], passes: [], notes: '' })
export const createPlay = (board: BoardState): Play => ({ initialPlayers: structuredClone(board.players), initialBall: board.ballHandlerId, steps: [{ ...emptyStep(), annotations: structuredClone(board.strokes) }], notes: '' })
export const distance = (a: Point, b: Point) => Math.hypot(a.x-b.x, a.y-b.y)
export const pathLength = (points: Point[]) => points.slice(1).reduce((sum, p, i) => sum + distance(points[i], p), 0)

export function along(points: Point[], fraction: number): Point {
  if (!points.length) return { x: .5, y: .5 }
  let remaining = pathLength(points) * Math.max(0, Math.min(1, fraction))
  for (let i = 1; i < points.length; i++) {
    const length = distance(points[i-1], points[i])
    if (length && remaining <= length) {
      const t = remaining / length
      return { x: points[i-1].x + (points[i].x-points[i-1].x)*t, y: points[i-1].y + (points[i].y-points[i-1].y)*t }
    }
    remaining -= length
  }
  return points[points.length-1]
}

export function positionAt(players: PlayerMarker[], movements: ScheduledMovement[], id: string, time: number): Point {
  let point: Point = players.find(p => p.id === id) || { x: .5, y: .5 }
  for (const movement of movements.filter(m => m.playerId === id)) {
    if (time < movement.start) break
    point = along(movement.points, (time-movement.start)/movement.duration)
  }
  return point
}

export function scheduleStep(players: PlayerMarker[], ball: string | null, step: PlayStep, speed = .24): Schedule {
  const endTimes = new Map<string, number>()
  const positions = new Map(players.map(p => [p.id, { x: p.x, y: p.y }]))
  const movements: ScheduledMovement[] = step.movements.map(m => {
    const points = [...m.points]
    // Join edited routes to the recomputed starting position without shifting their endpoints.
    points[0] = positions.get(m.playerId) || points[0]
    const duration = Math.max(.15, pathLength(points)/Math.max(.01,speed))
    const hold = m.style === 'screen' ? .5 : 0
    const start = endTimes.get(m.playerId) ?? .35
    endTimes.set(m.playerId, start+duration+hold)
    positions.set(m.playerId, points.at(-1)!)
    return { ...m, points, start, duration, hold }
  })
  const conflicts: string[] = []
  const passes: ScheduledPass[] = []
  let handler = ball
  let previousCatch = 0
  for (const pass of step.passes) {
    const segment = movements.find(m => m.id === pass.segmentId)
    if (pass.from !== handler) { conflicts.push('Passer does not have possession. Repair or remove the pass.'); continue }
    if (pass.to === pass.from || !players.some(p => p.id === pass.to)) { conflicts.push('Choose a different receiver.'); continue }
    if (pass.segmentId && (!segment || segment.playerId !== pass.to)) { conflicts.push('A pass target was removed. Choose a new receiver path.'); continue }
    const catchAt = segment ? segment.start + segment.duration * pass.fraction : previousCatch + .35
    const release = catchAt-.35
    if (release < previousCatch - .00001) { conflicts.push('This catch is too early for the possession sequence. Move its catch point later.'); continue }
    passes.push({ ...pass, release, catchAt, origin: positionAt(players, movements, pass.from, release), target: positionAt(players, movements, pass.to, catchAt) })
    handler = pass.to
    previousCatch = catchAt
  }
  return { movements, passes, conflicts, duration: Math.max(.35, ...movements.map(m => m.start+m.duration+m.hold), ...passes.map(p => p.catchAt)) }
}

export function stepStart(play: Play, index: number) {
  let players = structuredClone(play.initialPlayers)
  let ball = play.initialBall
  for (const step of play.steps.slice(0, index)) {
    const schedule = scheduleStep(players, ball, step, play.speed)
    players = players.map(p => ({ ...p, ...positionAt(players, schedule.movements, p.id, schedule.duration) }))
    ball = schedule.passes.at(-1)?.to || ball
  }
  return { players, ball }
}

export function frame(players: PlayerMarker[], ball: string | null, schedule: Schedule, time: number) {
  const markers = players.map(p => ({ ...p, ...positionAt(players, schedule.movements, p.id, time) }))
  let handler = ball
  let looseBall: Point | undefined
  for (const pass of schedule.passes) {
    if (time >= pass.catchAt) handler = pass.to
    else if (time >= pass.release) { handler = null; looseBall = along([pass.origin, pass.target], (time-pass.release)/.35); break }
  }
  return { players: markers, ballHandlerId: handler, looseBall }
}

export function movementStrokes(schedule: Schedule, initialBall: string | null): Stroke[] {
  return schedule.movements.flatMap(m => {
    const boundaries = [m.start, ...schedule.passes.flatMap(p => [p.release,p.catchAt]).filter(t => t > m.start && t < m.start+m.duration), m.start+m.duration].sort((a,b) => a-b)
    return boundaries.slice(1).map((end,index) => {
      const start = boundaries[index], middle = (start+end)/2
      let handler = initialBall
      for (const pass of schedule.passes) {
        if (middle >= pass.catchAt) handler = pass.to
        else if (middle >= pass.release) { handler = null; break }
      }
      const from = (start-m.start)/m.duration, to = (end-m.start)/m.duration
      const count = Math.max(2, Math.ceil((to-from)*pathLength(m.points)*100))
      return { id: `${m.id}:${index}`, kind: 'arrow', color: themeColors.midnight, style: handler === m.playerId ? 'dribble' : 'cut', points: Array.from({ length: count+1 },(_,i) => along(m.points, from+(to-from)*i/count)) }
    })
  })
}
